/**
 * 租户时区下的日界。
 *
 * ★ 不能用 `new Date().setHours(0,0,0,0)` —— 那取的是**服务器**时区。
 *
 * 生产上 API 跑在 UTC,而租户有自己的时区(比如 Pacific/Auckland,UTC+12/+13)。
 * 「今天」会整整差 8 小时:马来西亚时间早上 7 点送的单,
 * 按 UTC 算还属于「昨天」,司机的收入页和调度的今日战况都会少算,
 * 到了当地时间晚上 8 点之后又开始把明天的单算进来。
 *
 * 这种错法不会报错、不会崩,只是每天的数字都不对 ——
 * 而且只在部署到别的时区之后才出现,本地开发永远发现不了
 * (开发机的时区通常和租户一致)。
 */

/** 取某个时区当前时刻的各时间分量 */
function partsIn(tz: string, at: Date): { y: number; m: number; d: number } {
  // en-CA 给出 YYYY-MM-DD,最省解析
  const s = at.toLocaleDateString('en-CA', { timeZone: tz });
  const [y, m, d] = s.split('-').map(Number);
  return { y: y!, m: m!, d: d! };
}

/**
 * 该时区「今天零点」对应的 UTC 时刻。
 *
 * 做法是二分逼近:先按 UTC 猜一个,再用目标时区读回来看差多少。
 * 直接算偏移量在夏令时切换那天会错(偏移量本身在变)。
 */
export function startOfDayIn(tz: string, at: Date = new Date()): Date {
  const { y, m, d } = partsIn(tz, at);

  // 先取该日期的 UTC 零点作为起点
  let guess = Date.UTC(y, m - 1, d, 0, 0, 0, 0);

  // 用目标时区读回来,差多少就补多少。两次足够收敛 ——
  // 第一次修正偏移,第二次处理修正后跨过夏令时边界的情况
  for (let i = 0; i < 2; i++) {
    const seen = partsIn(tz, new Date(guess));
    const seenLocal = Date.UTC(seen.y, seen.m - 1, seen.d, 0, 0, 0, 0);
    const targetLocal = Date.UTC(y, m - 1, d, 0, 0, 0, 0);
    const drift = targetLocal - seenLocal;
    if (drift === 0) {
      // 日期对上了,还要把当天已过的小时数减掉
      const hh = Number(
        new Date(guess).toLocaleString('en-GB', {
          timeZone: tz,
          hour: '2-digit',
          hour12: false,
        }),
      );
      const mm = new Date(guess).toLocaleString('en-GB', {
        timeZone: tz,
        minute: '2-digit',
      });
      guess -= (hh % 24) * 3600_000 + Number(mm) * 60_000;
      // 再确认一次日期没被减到前一天
      const check = partsIn(tz, new Date(guess));
      if (check.d !== d) guess += 3600_000;
      break;
    }
    guess += drift;
  }

  return new Date(guess);
}

/**
 * 「某个时区的某一天某个钟点」对应的 UTC 时刻。
 *
 * ★ 这个函数是被一个真 bug 逼出来的。
 *
 *   排班原来是这么算的:拿 `${date}T12:00:00Z`(**UTC 中午**)当锚点,
 *   再问 `startOfDayIn` 「这一天的零点是几时」。注释写着
 *   「用当天中午当锚点,避开日界附近的歧义」—— 想法是对的,
 *   但**中午取的是 UTC 的中午,不是当地的中午**。
 *
 *   偏移在 +12 及以上的时区(奥克兰、斐济、堪察加),UTC 中午
 *   已经是当地**第二天的零点**了 —— 于是整个班次被排到了第二天。
 *   在吉隆坡(+8)它只是让班次晚 8 小时开始、仍然落在当天,
 *   所以一直没人发现;搬到奥克兰之后,司机点签到收到的是
 *   「还没到签到时间,181 分钟后可以签」。
 *
 * ★ 做法是标准的两趟逼近:先当成 UTC 猜一个,用目标时区读回来看差多少,
 *   补上差值再读一次。第二趟是为了夏令时切换那天 —— 偏移量本身在变,
 *   一趟修正会落在切换点的另一侧。
 */
export function zonedDateTime(tz: string, date: string, hhmm: string): Date {
  const [y, mo, d] = date.split('-').map(Number);
  const [h, mi] = hhmm.split(':').map(Number);
  const wanted = Date.UTC(y!, (mo ?? 1) - 1, d ?? 1, h ?? 0, mi ?? 0);

  let guess = wanted;
  for (let i = 0; i < 2; i++) {
    const seen = new Date(guess).toLocaleString('sv-SE', { timeZone: tz }); // YYYY-MM-DD HH:mm:ss
    const [sd, st] = seen.split(' ');
    const [sy, sm, sdd] = sd!.split('-').map(Number);
    const [sh, smi] = st!.split(':').map(Number);
    const drift = wanted - Date.UTC(sy!, sm! - 1, sdd!, sh!, smi!);
    if (drift === 0) break;
    guess += drift;
  }
  return new Date(guess);
}

/** 该时区「本周一零点」对应的 UTC 时刻 */
export function startOfWeekIn(tz: string, at: Date = new Date()): Date {
  const today = startOfDayIn(tz, at);
  // 用目标时区判断星期几,不能用服务器的 getDay()
  const weekday = new Date(today.getTime() + 12 * 3600_000).toLocaleDateString('en-US', {
    timeZone: tz,
    weekday: 'short',
  });
  const order = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const idx = Math.max(0, order.indexOf(weekday));
  return startOfDayIn(tz, new Date(today.getTime() - idx * 24 * 3600_000));
}

/** 该时区下「今天是几月几号」,`YYYY-MM-DD` */
export function dayIn(tz: string, at: Date = new Date()): string {
  return at.toLocaleDateString('en-CA', { timeZone: tz });
}

/**
 * `YYYY-MM-DD` 的后一天。
 *
 * ★ 用 UTC 加一天,不用本地时间:`new Date('2026-03-29')` 在有夏令时的
 *   时区里加 24 小时可能还停在同一天(那天有 25 小时),或者直接跳过一天。
 *   这里只是在**日历上**往后翻一页,和时区无关。
 */
export function nextDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  const t = new Date(Date.UTC(y!, (m ?? 1) - 1, (d ?? 1) + 1));
  return t.toISOString().slice(0, 10);
}

/**
 * `YYYY-MM-DD` 的前一天。
 *
 * ★ 和 `nextDay` 同一个道理:在**日历上**往前翻一页,和时区无关。
 *   「减 24 小时」在夏令时切换那天会原地不动或者跨过两天。
 *
 * ★ 为什么需要它:所有「昨天那个跨夜班现在还在进行中吗」的判断都要用
 *   (排班那边有两处)。原来两处各写了一份,而同一个值有两个来源
 *   就一定有一个是错的 —— 这套代码里已经为这件事付过几次代价。
 */
export function prevDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  const t = new Date(Date.UTC(y!, (m ?? 1) - 1, (d ?? 1) - 1));
  return t.toISOString().slice(0, 10);
}

/**
 * 「期望送达日期」的截止时刻 —— 那一天在该时区的**最后一刻**。
 *
 * ★ 存的是时刻不是日期,因为库里那一列是 timestamptz,而且异常规则
 *   (「货在车上却过了预计送达时间」)本来就是按时刻比的。
 *
 * ★ 取次日零点减 1 毫秒,不是「当天 23:59:59」:后者在夏令时切换那天
 *   会差一小时,而这一列决定的是「这单算不算逾期」——
 *   差一小时的后果是司机端在早上多亮一条提醒,他没法反驳也没法消掉。
 */
export function endOfDayIn(tz: string, day: string): Date {
  return new Date(zonedDateTime(tz, nextDay(day), '00:00').getTime() - 1);
}

/**
 * 新单默认期望哪天送到。
 *
 * ★ 用户原话:「默认就是当日达,然后可以选次日达……下午六点之后下的单
 *   默认就是次日达」。
 *
 *   傍晚之后录的单本来就是**明天的活**:司机那会儿多半已经收车了,
 *   而把它算成今天的,第二天早上司机端就会亮一条「昨天没送完」——
 *   一条每天都亮的提醒和不存在没有区别,真出问题那天他也不会看。
 *
 * ★ 这条规则只在**下单这一刻**起作用一次,把结果写进数据。
 *   以前它是散在司机端、每次渲染都重算一遍的 ——
 *   那样「这单算哪天的」取决于谁在什么时候打开了 App,
 *   而调度在管理端看到的又是另一个答案。
 */
export function defaultExpectedDay(tz: string, at: Date = new Date(), eveningHour = 18): string {
  const hh = Number(at.toLocaleString('en-GB', { timeZone: tz, hour: '2-digit', hour12: false }));
  const today = dayIn(tz, at);
  return hh >= eveningHour ? nextDay(today) : today;
}
